
import { useEffect } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { ActivePopup } from './types';

interface UseRealtimePopupsProps {
  setActivePopups: React.Dispatch<React.SetStateAction<ActivePopup[]>>;
  checkDatabasePopups: () => Promise<void>;
}

export const useRealtimePopups = ({ setActivePopups, checkDatabasePopups }: UseRealtimePopupsProps) => {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) {
      console.log('No user found, skipping realtime setup');
      return;
    }

    console.log('Setting up real-time subscription for user:', user.id);
    
    // Initial check for existing popups
    checkDatabasePopups().catch(error => {
      console.error('Error in initial popup check:', error);
    });
    
    // Set up real-time subscription for new popups
    const channel = supabase
      .channel('broadcast_popups_realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'broadcast_popups'
        },
        (payload) => {
          console.log('New broadcast popup received via realtime:', payload);
          
          if (!payload.new || typeof payload.new !== 'object') {
            console.warn('Invalid payload received:', payload);
            return;
          }
          
          const newPopup = payload.new as any;
          
          // Validate required fields
          if (!newPopup.popup_id || !newPopup.title || !newPopup.message) {
            console.warn('Popup missing required fields:', newPopup);
            return;
          }
          
          // Only show if user hasn't dismissed it and it's active
          const dismissedByUsers = Array.isArray(newPopup.dismissed_by_users) ? newPopup.dismissed_by_users : [];
          const isDismissedByUser = dismissedByUsers.includes(user.id);
          
          if (newPopup.is_active && !isDismissedByUser) {
            const popupContent: ActivePopup = {
              id: newPopup.popup_id,
              title: newPopup.title,
              message: newPopup.message,
              style: (newPopup.style || 'windows95') as any,
              priority: (newPopup.priority || 'medium') as any,
              dismissible: Boolean(newPopup.dismissible),
              persistForAllUsers: true,
              dbId: newPopup.id
            };

            setActivePopups(prev => {
              // Check if already exists to prevent duplicates
              const exists = prev.find(p => p.dbId === newPopup.id);
              if (exists) {
                console.log('Popup already exists, skipping:', newPopup.id);
                return prev;
              }
              
              console.log('Adding realtime popup to active list:', popupContent);
              return [...prev, popupContent];
            });
          } else {
            console.log('Popup not shown - dismissed by user or not active:', {
              is_active: newPopup.is_active,
              isDismissedByUser,
              popup_id: newPopup.popup_id
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'broadcast_popups'
        },
        (payload) => {
          console.log('Broadcast popup updated via realtime:', payload);
          
          if (!payload.new || typeof payload.new !== 'object') {
            console.warn('Invalid update payload received:', payload);
            return;
          }
          
          const updatedPopup = payload.new as any;
          
          // If popup was dismissed by this user or deactivated, remove it
          const dismissedByUsers = Array.isArray(updatedPopup.dismissed_by_users) ? updatedPopup.dismissed_by_users : [];
          const isDismissedByUser = dismissedByUsers.includes(user.id);
          
          if (!updatedPopup.is_active || isDismissedByUser) {
            console.log('Removing popup due to update:', updatedPopup.id);
            setActivePopups(prev => prev.filter(p => p.dbId !== updatedPopup.id));
          }
        }
      )
      .subscribe((status) => {
        console.log('Realtime subscription status:', status);
        if (status === 'SUBSCRIBED') {
          console.log('Successfully subscribed to broadcast_popups realtime updates');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('Error subscribing to realtime channel');
        }
      });

    return () => {
      console.log('Cleaning up realtime subscription');
      supabase.removeChannel(channel);
    };
  }, [user, checkDatabasePopups, setActivePopups]);
};
